import * as Sentry from '@sentry/react';
import { debugEnabled } from '../config';

/**
 * Wraps any error with a stable, searchable name for Sentry grouping.
 * Preserves the original error message and stores the original as `cause`.
 */
export class NamedError extends Error {
	cause: unknown;

	constructor(name: string, cause: unknown) {
		const originalError = cause instanceof Error ? cause : new Error(String(cause));
		super(originalError.message);
		this.name = name;
		this.cause = cause;
		if (originalError.stack) {
			this.stack = originalError.stack;
		}
	}
}

export function normalizeError(error: unknown): Error {
	// If it's already an Error instance, return as is
	if (error instanceof Error) {
		return error;
	}

	// Handle null/undefined explicitly
	if (error === null) {
		return new Error('Error was null');
	}
	if (error === undefined) {
		return new Error('Error was undefined');
	}

	// For objects, try to get a meaningful string representation
	if (typeof error === 'object') {
		try {
			return new Error(JSON.stringify(error));
		} catch {
			return new Error('[Object cannot be stringified]');
		}
	}

	// For other primitives, convert to string
	return new Error(String(error));
}

/**
 * Logs an info message to both console (if debug enabled) and Sentry.
 *
 * ⚠️ IMPORTANT: Do not log PII (Personally Identifiable Information) such as:
 * - Email addresses, phone numbers, or physical addresses
 * - Full names, usernames, or nicknames
 * - Passwords, tokens, or authentication credentials
 * - Any other sensitive user data as defined in https://hello.atlassian.net/wiki/spaces/PMP/pages/139161585/Policy+-+Data+Classification
 *
 * Ensure all potentially sensitive data is sanitized before logging.
 */
export function logInfo(msg: string, ...args: any[]): void {
	if (debugEnabled) {
		// eslint-disable-next-line no-console
		console.log(msg, ...args);
	}
	Sentry.captureEvent({ message: msg, level: 'info', extra: { ...args } });
}

/**
 * Logs a warning message to both console (if debug enabled) and Sentry.
 *
 * ⚠️ IMPORTANT: Do not log PII (Personally Identifiable Information) such as:
 * - Email addresses, phone numbers, or physical addresses
 * - Full names, usernames, or nicknames
 * - Passwords, tokens, or authentication credentials
 * - Any other sensitive user data as defined in https://hello.atlassian.net/wiki/spaces/PMP/pages/139161585/Policy+-+Data+Classification
 *
 * Ensure all potentially sensitive data is sanitized before logging.
 */
export function logWarn(msg: string, ...args: any[]): void {
	if (debugEnabled) {
		// eslint-disable-next-line no-console
		console.warn(msg, ...args);
	}

	Sentry.captureEvent({ message: msg, level: 'warning', extra: { ...args } });
}

interface LogErrorProps {
	error: unknown;
	impact?: string;
	extra?: Record<string, unknown>;
	tags?: Record<string, string>;
	level?: 'error' | 'warning' | 'info';
	context?: Record<string, Record<string, unknown>>;
}

/**
 * Logs an error to both console (if debug enabled) and Sentry.
 *
 * ⚠️ IMPORTANT: Do not log PII (Personally Identifiable Information) such as:
 * - Email addresses, phone numbers, or physical addresses
 * - Full names, usernames, or nicknames
 * - Passwords, tokens, or authentication credentials
 * - Any other sensitive user data as defined in https://hello.atlassian.net/wiki/spaces/PMP/pages/139161585/Policy+-+Data+Classification
 *
 * Ensure all potentially sensitive data is sanitized before logging.
 */
export function logError({ error, impact, extra, tags, context }: LogErrorProps): void {
	const errorObject = normalizeError(error);

	if (debugEnabled) {
		// eslint-disable-next-line no-console
		console.error(impact, errorObject, extra);
	}

	Sentry.captureException(errorObject, {
		tags: {
			...tags,
		},
		extra: {
			impact,
			...(extra ?? {}),
		},
		contexts: {
			...context,
		},
	});
}
